import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer,
} from 'recharts';
import { styles, colors, radius } from '../../styles/theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Backend trend response: [{hafta, lokasyon_id, food_cost_pct}, ...]
// recharts wide-format: [{hafta: '2026-W18', 'Inga Burger': 38, 'Köfte': 35}, ...]
function transformTrend(trend, lokasyonlar) {
  const lokAdMap = Object.fromEntries(lokasyonlar.map(l => [l.lokasyon_id, l.lokasyon_ad]));
  const grouped = {};
  trend.forEach(row => {
    if (!grouped[row.hafta]) grouped[row.hafta] = { hafta: row.hafta };
    const lokAd = lokAdMap[row.lokasyon_id];
    if (lokAd) grouped[row.hafta][lokAd] = row.food_cost_pct;
  });
  return Object.values(grouped).sort((a, b) => a.hafta.localeCompare(b.hafta));
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Reçete satırı altında açılan detay paneli — hammadde kırılımı + 3 metrik özeti.
 * Lazy fetch'lenen `detail` (cost endpoint response) verisini kullanır.
 */
const RecipeDetailPanel = ({ detail, loading, recipeId, recipe }) => {
  if (loading && !detail) {
    return <div style={{ padding: 16, textAlign: 'center', color: '#666' }}>Detay yükleniyor...</div>;
  }
  if (!detail) {
    return <div style={{ padding: 16, color: '#999' }}>Detay yok</div>;
  }
  if (detail.error) {
    return <div style={{ padding: 16, color: '#c62828' }}>{detail.error}</div>;
  }

  const breakdown = detail.breakdown || [];
  const yenilebilirFC = detail.yenilebilir_fc_ratio;
  const ambalajOran = detail.ambalaj_oran;
  const toplamFC = detail.toplam_cost_ratio ?? detail.food_cost_ratio;
  const yenilebilirMaliyet = detail.yenilebilir_maliyet;
  const ambalajMaliyet = detail.ambalaj_maliyet;
  const porsiyonMaliyet = detail.porsiyon_maliyet ?? detail.toplam_maliyet;

  // Kategori (ambalaj mı, yenilebilir mi)
  const isAmbalaj = (item) => {
    const k = (item.kategori || '').toLowerCase();
    return k === 'ambalaj';
  };

  const fmt = (n, d = 2) => (n != null && !isNaN(n)) ? n.toFixed(d) : '-';

  return (
    <div style={{ padding: '14px 18px' }}>
      {/* Özet Kartlar */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        {yenilebilirFC != null && (
          <MetricCard label="Yenilebilir FC %" value={`%${fmt(yenilebilirFC, 1)}`}
            sub={yenilebilirMaliyet != null ? `₺${fmt(yenilebilirMaliyet)}` : null}
            color="#2e7d32" bg="#e8f5e9" hint="endüstri standart food cost" />
        )}
        {ambalajOran != null && ambalajOran > 0 && (
          <MetricCard label="Ambalaj %" value={`%${fmt(ambalajOran, 1)}`}
            sub={ambalajMaliyet != null ? `₺${fmt(ambalajMaliyet)}` : null}
            color="#e65100" bg="#fff3e0" hint="paket maliyeti" />
        )}
        {toplamFC != null && (
          <MetricCard label="Toplam FC %" value={`%${fmt(toplamFC, 1)}`}
            sub={porsiyonMaliyet != null ? `₺${fmt(porsiyonMaliyet)}` : null}
            color="#1565c0" bg="#e3f2fd" hint="paket dahil toplam" />
        )}
        <button
          onClick={(e) => { e.stopPropagation(); window.open(`/recete/duzenle?id=${recipeId}`, '_blank'); }}
          style={{
            marginLeft: 'auto', padding: '8px 14px', background: '#fff', color: '#1565c0',
            border: '1px solid #1565c0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            alignSelf: 'flex-start',
          }}>
          ✎ Reçeteyi Düzenle
        </button>
      </div>

      {/* Hammadde Tablosu */}
      {breakdown.length === 0 ? (
        <div style={{ padding: 12, color: '#999', fontSize: 12 }}>Bu reçetenin hammadde kırılımı yok.</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f5f7fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: 8, textAlign: 'left' }}>Malzeme</th>
                <th style={{ padding: 8, textAlign: 'center' }}>Tip</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Miktar</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Fire %</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Brüt</th>
                <th style={{ padding: 8, textAlign: 'right' }}>₺/Birim</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Satır Maliyet</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((item, idx) => {
                const ambalaj = isAmbalaj(item);
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px 8px' }}>
                      <div style={{ fontWeight: 500 }}>{item.malzeme_ad || item.ad || `#${item.malzeme_id ?? '?'}`}</div>
                      {item.kategori && (
                        <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{item.kategori}</div>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '1px 6px', borderRadius: 8,
                        fontSize: 10, fontWeight: 600,
                        background: ambalaj ? '#fff3e0' : '#e8f5e9',
                        color: ambalaj ? '#e65100' : '#2e7d32',
                      }}>
                        {ambalaj ? 'Ambalaj' : 'Yenilebilir'}
                      </span>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(item.miktar, 3)} {item.birim}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: item.fire_orani > 0 ? '#e65100' : '#888' }}>
                      {item.fire_orani > 0 ? `%${fmt(item.fire_orani, 1)}` : '-'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(item.brut_miktar, 3)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>₺{fmt(item.birim_fiyat)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                      ₺{fmt(item.satir_maliyet)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: '#f5f7fa', fontWeight: 700 }}>
                <td colSpan={6} style={{ padding: 8, textAlign: 'right' }}>TOPLAM:</td>
                <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>₺{fmt(porsiyonMaliyet)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* POS fiyatı bilgisi */}
      {recipe?.pos_satis_fiyati && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
          POS Satış Fiyatı: <strong style={{ color: '#1565c0' }}>₺{fmt(recipe.pos_satis_fiyati)}</strong>
          {recipe.dukkan_kar != null && (
            <span style={{ marginLeft: 16 }}>
              Birim Kâr: <strong style={{ color: recipe.dukkan_kar >= 0 ? '#2e7d32' : '#c62828' }}>₺{fmt(recipe.dukkan_kar)}</strong>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, sub, color, bg, hint }) => (
  <div style={{ background: bg, padding: '10px 14px', borderRadius: 6, minWidth: 130 }}>
    <div style={{ fontSize: 10, color: color, fontWeight: 600, opacity: 0.8 }}>{label}</div>
    <div style={{ fontSize: 18, color: color, fontWeight: 700, marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: color, opacity: 0.85 }}>{sub}</div>}
    {hint && <div style={{ fontSize: 9, color: color, opacity: 0.6, marginTop: 2 }}>{hint}</div>}
  </div>
);

/**
 * Food Cost & Karlılık Panosu
 * - Özet kartlar: ortalama food cost %, karlılık, en pahalı/ucuz reçete
 * - Tablo: tüm reçeteler + satış fiyatı + karlılık
 * - Renk kodlu: <%28 yeşil, %28-35 sarı, >%35 kırmızı
 * - Transfer fiyat verisi ile eşleştirme
 * - Satıra tıklayarak inline reçete detayı (hammaddeler + 3 metrik)
 */
const FoodCostDashboard = () => {
  const [lokasyonFiltre, setLokasyonFiltre] = useState('');
  const [firmalar, setFirmalar] = useState([]);
  const [lokasyonData, setLokasyonData] = useState(null);
  const [varyansData, setVaryansData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [costData, setCostData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipFiltre, setTipFiltre] = useState('');
  const [markaFiltre, setMarkaFiltre] = useState('');
  const [sortField, setSortField] = useState('porsiyon_maliyet');
  const [sortDir, setSortDir] = useState('desc');
  // Satır genişletme: tıklanan reçetenin id'si + lazy-load edilmiş cost detayı
  const [expandedId, setExpandedId] = useState(null);
  const [detailCache, setDetailCache] = useState({});  // id -> cost response
  const [detailLoading, setDetailLoading] = useState(false);

  const handleToggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (detailCache[id]) return;  // zaten cache'te
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/recipes/${id}/cost`, { headers: getAuthHeaders() });
      const data = await res.json();
      setDetailCache(prev => ({ ...prev, [id]: data }));
    } catch (err) {
      setDetailCache(prev => ({ ...prev, [id]: { error: 'Detay yüklenemedi' } }));
    }
    setDetailLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    const headers = getAuthHeaders();
    // Firma filtre seçenekleri DB'den (Anayasa M.1 — hardcode yasak)
    fetch(`${API_URL}/api/firmalar`, { headers })
      .then(r => (r.ok ? r.json() : []))
      .then(data => setFirmalar(Array.isArray(data) ? data : (data?.firmalar || data?.data || [])))
      .catch(() => setFirmalar([]));
    Promise.all([
      fetch(`${API_URL}/api/recipes-summary`, { headers }).then(r => r.json()).catch(() => ({})),
      fetch(`${API_URL}/api/transfer-prices`, { headers }).then(r => r.json()).catch(() => [])
    ]).then(([summaryData, tfData]) => {
      const summaryObj = summaryData && typeof summaryData === 'object' ? summaryData : {};
      setSummary(summaryObj);

      // Transfer fiyat map (malzeme_ad -> data)
      const tfMap = {};
      (Array.isArray(tfData) ? tfData : []).forEach(tf => {
        tfMap[tf.malzeme_ad?.toLowerCase?.()?.trim?.()] = tf;
      });

      // recipes-summary endpoint'inden gelen listeyi kullan (N+1 cost çağrılarını kaldırır)
      const receteListesi =
        (Array.isArray(summaryObj.recipes) && summaryObj.recipes) ||
        (Array.isArray(summaryObj.receteler) && summaryObj.receteler) ||
        (Array.isArray(summaryObj.data) && summaryObj.data) ||
        (Array.isArray(summaryData) && summaryData) ||
        [];

      const normalized = receteListesi.map((r) => {
        const adKey = (r.ad || r.urun_malzeme_ad || '').toLowerCase().trim();
        // TF lookup sadece exact match — "Por." prefix kaldırma yanlış eşleşmeye sebep oluyordu
        // (Por. Mostar tabağı → Mostar yarı mamul TF'si gibi). Sadece fallback olarak kullanılır.
        const tf = tfMap[adKey] || null;
        const sonMaliyet = r.son_maliyet ?? r.porsiyon_maliyet ?? r.toplam_maliyet ?? tf?.uretim_maliyeti ?? null;

        // Satış fiyati: POS fiyati (gerçek menü fiyatı) — tek kaynak
        const efektifSatisFiyati = r.pos_satis_fiyati || null;

        // Food cost + kar — TF snapshot'ı bypass et, her zaman gerçek zamanlı hesapla
        // (TF.food_cost_pct/dukkan_kar değerleri reçete maliyeti değişince güncellenmiyor)
        let foodCostPct = null;
        let kar = null;
        let karPct = null;
        if (sonMaliyet && efektifSatisFiyati && efektifSatisFiyati > 0) {
          foodCostPct = parseFloat(((sonMaliyet / efektifSatisFiyati) * 100).toFixed(1));
          kar = parseFloat((efektifSatisFiyati - sonMaliyet).toFixed(2));
          karPct = parseFloat((((efektifSatisFiyati - sonMaliyet) / efektifSatisFiyati) * 100).toFixed(1));
        }

        return {
          ...r,
          recete_kodu: r.recete_kodu || r.kod || `R-${r.id}`,
          ad: r.ad || r.urun_malzeme_ad || '-',
          tip: r.tip || 'TABAK',
          marka: r.marka || null,
          porsiyon_maliyet: sonMaliyet,
          pos_satis_fiyati: efektifSatisFiyati,
          satis_fiyati: efektifSatisFiyati,  // backward-compat
          dukkan_kar: kar,
          dukkan_kar_pct: karPct,
          food_cost_pct: foodCostPct
        };
      });

      setCostData(normalized);
      setLoading(false);
    });
  }, []);

  // Lokasyon karşılaştırma + haftalık trend verisi (Task 3 endpoint)
  useEffect(() => {
    const today = new Date();
    const baslangic = new Date(today);
    baslangic.setDate(today.getDate() - 30);
    const fmt = (d) => d.toISOString().slice(0, 10);

    const params = `baslangic=${fmt(baslangic)}&bitis=${fmt(today)}${lokasyonFiltre ? `&firma=${lokasyonFiltre}` : ''}`;
    const url = `${API_URL}/api/reports/food-cost-by-lokasyon?${params}`;

    let cancelled = false;
    fetch(url, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (!cancelled) setLokasyonData(data); })
      .catch(() => { if (!cancelled) setLokasyonData(null); });
    return () => { cancelled = true; };
  }, [lokasyonFiltre]);

  // Maliyet varyans verisi (kategori kırılımı + top sapan reçeteler)
  useEffect(() => {
    const today = new Date();
    const baslangic = new Date(today);
    baslangic.setDate(today.getDate() - 30);
    const fmt = (d) => d.toISOString().slice(0, 10);

    const url = `${API_URL}/api/reports/maliyet-varyans?baslangic=${fmt(baslangic)}&bitis=${fmt(today)}`;

    let cancelled = false;
    fetch(url, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (!cancelled) setVaryansData(data); })
      .catch(() => { if (!cancelled) setVaryansData(null); });
    return () => { cancelled = true; };
  }, []);

  // Filtre
  let filteredData = costData;
  if (tipFiltre) filteredData = filteredData.filter(d => d.tip === tipFiltre);
  if (markaFiltre) filteredData = filteredData.filter(d => d.marka === markaFiltre);

  // Sıralama
  const sortedData = [...filteredData].sort((a, b) => {
    const av = a[sortField] || 0;
    const bv = b[sortField] || 0;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => (
    <span style={{ fontSize: '10px', marginLeft: '4px', opacity: sortField === field ? 1 : 0.3 }}>
      {sortField === field ? (sortDir === 'desc' ? '▼' : '▲') : '↕'}
    </span>
  );

  // Stats
  const withCost = costData.filter(d => d.porsiyon_maliyet > 0);
  const avgCost = withCost.length > 0
    ? (withCost.reduce((sum, d) => sum + d.porsiyon_maliyet, 0) / withCost.length)
    : 0;
  const maxCost = withCost.length > 0 ? withCost.reduce((max, d) => d.porsiyon_maliyet > max.porsiyon_maliyet ? d : max, withCost[0]) : null;
  const minCost = withCost.length > 0 ? withCost.reduce((min, d) => d.porsiyon_maliyet < min.porsiyon_maliyet ? d : min, withCost[0]) : null;

  // Karlılık stats (sadece satış fiyatı olanlar)
  const withSatis = costData.filter(d => d.satis_fiyati > 0);
  const ortFoodCost = withSatis.length > 0
    ? (withSatis.reduce((sum, d) => sum + (d.food_cost_pct || 0), 0) / withSatis.length)
    : 0;
  const ortKarlilik = withSatis.length > 0
    ? (withSatis.reduce((sum, d) => sum + (d.dukkan_kar_pct || 0), 0) / withSatis.length)
    : 0;

  // Marka listesi
  const markalar = [...new Set(costData.map(d => d.marka).filter(Boolean))];

  const exportCSV = () => {
    const rows = [['Kod', 'Reçete Adi', 'Tip', 'Marka', 'Birim Maliyet', 'Satış Fiyati', 'Fiyat Kaynagi', 'Birim Kar', 'Karlilik %', 'Food Cost %']];
    sortedData.forEach(d => {
      rows.push([
        d.recete_kodu, d.ad, d.tip, d.marka || '-',
        d.porsiyon_maliyet ? d.porsiyon_maliyet.toFixed(2) : '-',
        d.satis_fiyati ? d.satis_fiyati.toFixed(2) : '-',
        d.pos_satis_fiyati ? 'POS' : d.satis_fiyati ? 'Manuel' : '-',
        d.dukkan_kar != null ? d.dukkan_kar.toFixed(2) : '-',
        d.dukkan_kar_pct != null ? d.dukkan_kar_pct.toFixed(1) : '-',
        d.food_cost_pct != null ? d.food_cost_pct.toFixed(1) : '-'
      ]);
    });
    const csv = rows.map(r => r.join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `food-cost-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getFoodCostColor = (ratio) => {
    if (!ratio) return { bg: '#e9ecef', color: '#495057' };
    if (ratio < 28) return { bg: '#d4edda', color: '#155724' };
    if (ratio <= 35) return { bg: '#fff3cd', color: '#856404' };
    if (ratio <= 50) return { bg: '#ffe0b2', color: '#e65100' };
    return { bg: '#f8d7da', color: '#721c24' };
  };

  const getKarColor = (pct) => {
    if (!pct || pct <= 0) return { bg: '#f8d7da', color: '#721c24' };
    if (pct < 30) return { bg: '#fff3cd', color: '#856404' };
    if (pct < 50) return { bg: '#d4edda', color: '#155724' };
    return { bg: '#c8e6c9', color: '#1b5e20' };
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${styles.primary}, ${styles.primaryDark})`,
        color: 'white', padding: '15px 25px'
      }}>
        <h1 style={{ margin: 0, fontSize: '20px' }}>📊 Food Cost & Karlılık Panosu</h1>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
          <span style={{ fontSize: '12px', opacity: 0.8 }}>
            {costData.length} reçete | {withSatis.length} satış fiyatlı
          </span>
          <button onClick={exportCSV} style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
            CSV Indir
          </button>
        </div>
      </div>

      {/* Lokasyon Filtre */}
      <div style={{ padding: '15px 20px 0', display: 'flex', gap: 12, alignItems: 'center' }}>
        <label style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>
          Lokasyon (Firma):
        </label>
        <select
          value={lokasyonFiltre}
          onChange={(e) => setLokasyonFiltre(e.target.value)}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #d0d5dd',
            fontSize: 14,
            background: 'white',
            cursor: 'pointer',
          }}
        >
          <option value="">Tümü (Konsolide)</option>
          {firmalar.filter(f => f.aktif !== false).map(f => (
            <option key={f.kod} value={f.kod}>{f.ad}</option>
          ))}
        </select>
        {lokasyonData?.konsolide && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#475569' }}>
            Konsolide FC%: <strong style={{ color: '#1a1f2e' }}>%{(lokasyonData.konsolide.food_cost_pct || 0).toFixed(1)}</strong>
            <span style={{ marginLeft: 12 }}>
              Ciro: <strong style={{ color: '#1a1f2e' }}>₺{(lokasyonData.konsolide.ciro_tl || 0).toLocaleString('tr-TR')}</strong>
            </span>
          </span>
        )}
      </div>

      {/* Lokasyon karşılaştırma bar chart */}
      {lokasyonData && lokasyonData.lokasyonlar && lokasyonData.lokasyonlar.length > 1 && (
        <div style={{
          background: '#fff', borderRadius: 8, padding: 20, margin: '15px 20px 0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          border: `1px solid ${colors.border}`,
        }}>
          <h3 style={{ margin: '0 0 14px 0', fontSize: 16, color: '#1a1f2e' }}>
            Lokasyon Karşılaştırma — Food Cost %
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={lokasyonData.lokasyonlar}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
              <XAxis dataKey="lokasyon_ad" tick={{ fontSize: 12 }} />
              <YAxis label={{ value: '%', angle: -90, position: 'insideLeft' }} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(v, name) => [`${v}%`, name]}
                labelStyle={{ color: '#1a1f2e' }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="theoretical_pct" fill="#82ca9d" name="Teorik FC%" />
              <Bar dataKey="food_cost_pct" fill="#ef4444" name="Gerçek FC% (zayi dahil)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 8 haftalık trend line chart */}
      {lokasyonData && lokasyonData.trend_haftalik && lokasyonData.trend_haftalik.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 8, padding: 20, margin: '15px 20px 0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          border: `1px solid ${colors.border}`,
        }}>
          <h3 style={{ margin: '0 0 14px 0', fontSize: 16, color: '#1a1f2e' }}>
            Haftalık Trend — Food Cost %
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={transformTrend(lokasyonData.trend_haftalik, lokasyonData.lokasyonlar)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
              <XAxis dataKey="hafta" tick={{ fontSize: 12 }} />
              <YAxis label={{ value: '%', angle: -90, position: 'insideLeft' }} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {lokasyonData.lokasyonlar.map((l, idx) => (
                <Line
                  key={l.lokasyon_id}
                  type="monotone"
                  dataKey={l.lokasyon_ad}
                  stroke={['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'][idx % 5]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Kategori dağılımı — 2 donut yan yana */}
      {varyansData && (
        (varyansData.kategori_kirilim_malzeme || []).length > 0 ||
        (varyansData.kategori_kirilim_urun_tipi || []).length > 0
      ) && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16,
          margin: '15px 20px 0',
        }}>
          {/* Malzeme kategori donut */}
          <div style={{ background: '#fff', borderRadius: 8, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: `1px solid ${colors.border}` }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#475569' }}>
              Maliyet Dağılımı — Malzeme Kategorisi
            </h4>
            {(varyansData.kategori_kirilim_malzeme || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={varyansData.kategori_kirilim_malzeme}
                    dataKey="tl"
                    nameKey="kategori"
                    cx="50%" cy="50%"
                    innerRadius={50} outerRadius={85}
                    paddingAngle={3}
                  >
                    {varyansData.kategori_kirilim_malzeme.map((_, idx) => (
                      <Cell key={idx} fill={['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'][idx % 6]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `₺${Number(v).toLocaleString('tr-TR')}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ padding: 20, color: '#9ca3af', fontSize: 13, textAlign: 'center' }}>
                Veri yok
              </div>
            )}
          </div>

          {/* Urun tipi donut */}
          <div style={{ background: '#fff', borderRadius: 8, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: `1px solid ${colors.border}` }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#475569' }}>
              Maliyet Dağılımı — Ürün Tipi
            </h4>
            {(varyansData.kategori_kirilim_urun_tipi || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={varyansData.kategori_kirilim_urun_tipi}
                    dataKey="tl"
                    nameKey="urun_tipi"
                    cx="50%" cy="50%"
                    innerRadius={50} outerRadius={85}
                    paddingAngle={3}
                  >
                    {varyansData.kategori_kirilim_urun_tipi.map((_, idx) => (
                      <Cell key={idx} fill={['#0ea5e9', '#84cc16', '#f97316', '#ec4899', '#6366f1', '#14b8a6'][idx % 6]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `₺${Number(v).toLocaleString('tr-TR')}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ padding: 20, color: '#9ca3af', fontSize: 13, textAlign: 'center' }}>
                Veri yok
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top sapan reçete tablosu */}
      {varyansData && varyansData.top_sapan_recipe && varyansData.top_sapan_recipe.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 8, padding: 20, margin: '15px 20px 0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          border: `1px solid ${colors.border}`,
        }}>
          <h3 style={{ margin: '0 0 6px 0', fontSize: 16, color: '#1a1f2e' }}>
            Sapan Reçeteler
          </h3>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
            Food cost % ortalamadan %10 yukarıda VE toplam ciroda ≥ %1 pay (false positive eler)
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Reçete</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>FC %</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Ciro Payı</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Satış (TL)</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Maliyet (TL)</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Adet</th>
              </tr>
            </thead>
            <tbody>
              {varyansData.top_sapan_recipe.map(r => (
                <tr key={r.recipe_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px 10px', fontWeight: 500 }}>{r.recipe_ad}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>
                    %{r.food_cost_pct}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>%{r.ciro_pay_pct}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                    ₺{Number(r.satis_tl).toLocaleString('tr-TR')}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                    ₺{Number(r.theoretical_tl).toLocaleString('tr-TR')}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{r.satis_adet}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Özet Kartları - 6 Kart */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', padding: '15px 20px' }}>
        <div style={{ background: '#e3f2fd', padding: '15px', borderRadius: radius.md, textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1565c0' }}>
            {summary?.toplam_recete || costData.length}
          </div>
          <div style={{ fontSize: '11px', color: '#1565c0', marginTop: '4px' }}>Toplam Reçete</div>
        </div>
        <div style={{ background: '#e8f5e9', padding: '15px', borderRadius: radius.md, textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
            ₺{avgCost.toFixed(0)}
          </div>
          <div style={{ fontSize: '11px', color: '#2e7d32', marginTop: '4px' }}>Ort. Birim Maliyet</div>
        </div>
        <div style={{
          background: getFoodCostColor(ortFoodCost).bg, padding: '15px', borderRadius: radius.md, textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: getFoodCostColor(ortFoodCost).color }}>
            %{ortFoodCost.toFixed(1)}
          </div>
          <div style={{ fontSize: '11px', color: getFoodCostColor(ortFoodCost).color, marginTop: '4px' }}>Ort. Food Cost</div>
        </div>
        <div style={{
          background: getKarColor(ortKarlilik).bg, padding: '15px', borderRadius: radius.md, textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: getKarColor(ortKarlilik).color }}>
            %{ortKarlilik.toFixed(1)}
          </div>
          <div style={{ fontSize: '11px', color: getKarColor(ortKarlilik).color, marginTop: '4px' }}>Ort. Karlılık</div>
        </div>
        <div style={{ background: '#f8d7da', padding: '15px', borderRadius: radius.md, textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#721c24' }}>
            {maxCost ? `₺${maxCost.porsiyon_maliyet.toFixed(0)}` : '-'}
          </div>
          <div style={{ fontSize: '10px', color: '#721c24', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            En Pahalı: {maxCost?.ad || '-'}
          </div>
        </div>
        <div style={{ background: '#d4edda', padding: '15px', borderRadius: radius.md, textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#155724' }}>
            {minCost ? `₺${minCost.porsiyon_maliyet.toFixed(2)}` : '-'}
          </div>
          <div style={{ fontSize: '10px', color: '#155724', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            En Ucuz: {minCost?.ad || '-'}
          </div>
        </div>
      </div>

      {/* Karlılık Dağılımı - Çubuk Grafik */}
      {!loading && withSatis.length > 0 && (
        <div style={{ padding: '0 20px 15px' }}>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '15px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>
              💰 Karlılık Dağılımı (Satış Fiyatı Olan Ürünler)
            </h3>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {[...withSatis].sort((a, b) => (b.dukkan_kar || 0) - (a.dukkan_kar || 0)).map((d, idx) => {
                const maxKar = Math.max(...withSatis.map(x => Math.abs(x.dukkan_kar || 0)));
                const pct = maxKar > 0 ? (Math.abs(d.dukkan_kar || 0) / maxKar) * 100 : 0;
                const isPositive = (d.dukkan_kar || 0) >= 0;
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                    <div style={{ width: '160px', fontSize: '11px', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.ad}
                    </div>
                    <div style={{ flex: 1, background: '#f0f0f0', borderRadius: '3px', height: '18px', overflow: 'hidden', position: 'relative' }}>
                      <div style={{
                        width: `${pct}%`, height: '100%', borderRadius: '3px',
                        background: isPositive ? (d.dukkan_kar_pct > 50 ? '#2e7d32' : '#66bb6a') : '#ef5350'
                      }} />
                    </div>
                    <div style={{ width: '70px', fontSize: '11px', fontWeight: 'bold', color: isPositive ? '#2e7d32' : '#c62828' }}>
                      ₺{(d.dukkan_kar || 0).toFixed(0)}
                    </div>
                    <div style={{
                      width: '50px', fontSize: '10px', fontWeight: 'bold', textAlign: 'center',
                      padding: '1px 4px', borderRadius: radius.md,
                      ...getKarColor(d.dukkan_kar_pct)
                    }}>
                      %{(d.dukkan_kar_pct || 0).toFixed(0)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Maliyet Dağılımı - Bar */}
      {!loading && withCost.length > 0 && (
        <div style={{ padding: '0 20px 15px' }}>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '15px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>📦 Maliyet Dağılımı (Top 15)</h3>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {[...withCost].sort((a, b) => b.porsiyon_maliyet - a.porsiyon_maliyet).slice(0, 15).map((d, idx) => {
                const pct = maxCost ? (d.porsiyon_maliyet / maxCost.porsiyon_maliyet) * 100 : 0;
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <div style={{ width: '150px', fontSize: '11px', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.ad}
                    </div>
                    <div style={{ flex: 1, background: '#f0f0f0', borderRadius: '3px', height: '16px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${pct}%`, height: '100%', borderRadius: '3px',
                        background: d.tip === 'YARI_MAMUL' ? '#1565c0' : '#c62828'
                      }} />
                    </div>
                    <div style={{ width: '80px', fontSize: '11px', fontWeight: 'bold' }}>
                      ₺{d.porsiyon_maliyet.toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '15px', marginTop: '8px', fontSize: '11px', color: colors.textSecondary }}>
              <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#1565c0', borderRadius: '2px', marginRight: '4px', verticalAlign: 'middle' }}></span> Yarı Mamul</span>
              <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#c62828', borderRadius: '2px', marginRight: '4px', verticalAlign: 'middle' }}></span> Tabak</span>
            </div>
          </div>
        </div>
      )}

      {/* Filtre & Tablo */}
      <div style={{ padding: '0 20px 20px' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={tipFiltre} onChange={e => setTipFiltre(e.target.value)}
            style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
            <option value="">Tüm Tipler</option>
            <option value="YARI_MAMUL">Yarı Mamul</option>
            <option value="TABAK">Tabak</option>
          </select>
          <select value={markaFiltre} onChange={e => setMarkaFiltre(e.target.value)}
            style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
            <option value="">Tüm Markalar</option>
            {markalar.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px', color: colors.textSecondary }}>
            <span style={{ fontWeight: 'bold' }}>Food Cost:</span>
            <span style={{ padding: '3px 8px', borderRadius: radius.sm, background: '#d4edda', color: '#155724' }}>{'<28% İdeal'}</span>
            <span style={{ padding: '3px 8px', borderRadius: radius.sm, background: '#fff3cd', color: '#856404' }}>28-35%</span>
            <span style={{ padding: '3px 8px', borderRadius: radius.sm, background: '#ffe0b2', color: '#e65100' }}>35-50%</span>
            <span style={{ padding: '3px 8px', borderRadius: radius.sm, background: '#f8d7da', color: '#721c24' }}>{'>50%'}</span>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: '12px', color: colors.textSecondary }}>
            {sortedData.length} / {costData.length} reçete
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Maliyetler hesaplanıyor... (Bu biraz sürebilir)</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '8px', width: '28px' }}></th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Kod</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Reçete Adı</th>
                <th style={{ padding: '8px', textAlign: 'center' }}>Tip</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Marka</th>
                <th style={{ padding: '8px', textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('porsiyon_maliyet')}>
                  Birim Maliyet <SortIcon field="porsiyon_maliyet" />
                </th>
                <th style={{ padding: '8px', textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('satis_fiyati')}>
                  Satış Fiyatı <SortIcon field="satis_fiyati" />
                </th>
                <th style={{ padding: '8px', textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('dukkan_kar')}>
                  Birim Kâr <SortIcon field="dukkan_kar" />
                </th>
                <th style={{ padding: '8px', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('dukkan_kar_pct')}>
                  Karlılık <SortIcon field="dukkan_kar_pct" />
                </th>
                <th style={{ padding: '8px', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('food_cost_pct')}>
                  Food Cost <SortIcon field="food_cost_pct" />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map(d => {
                const fcColor = getFoodCostColor(d.food_cost_pct);
                const karColor = getKarColor(d.dukkan_kar_pct);
                const isExpanded = expandedId === d.id;
                const detail = detailCache[d.id];

                return (
                  <React.Fragment key={d.id}>
                  <tr
                      style={{
                        borderBottom: `1px solid ${colors.border}`,
                        cursor: 'pointer',
                        background: isExpanded ? '#eef5ff' : 'transparent',
                      }}
                      onClick={() => handleToggleExpand(d.id)}
                      onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f8f9fa'; }}
                      onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ padding: '6px 8px', textAlign: 'center', color: '#888', fontSize: '12px', userSelect: 'none' }}>
                      {isExpanded ? '▼' : '▶'}
                    </td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 'bold', color: styles.primary, fontSize: '11px' }}>{d.recete_kodu}</td>
                    <td style={{ padding: '6px 8px' }}>{d.ad}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: radius.lg, fontSize: '10px', fontWeight: 'bold',
                        background: d.tip === 'YARI_MAMUL' ? '#e3f2fd' : '#fce4ec',
                        color: d.tip === 'YARI_MAMUL' ? '#1565c0' : '#c62828'
                      }}>
                        {d.tip === 'YARI_MAMUL' ? 'Y.Mamul' : 'Tabak'}
                      </span>
                    </td>
                    <td style={{ padding: '6px 8px', color: colors.textSecondary, fontSize: '11px' }}>{d.marka || '-'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>
                      {d.porsiyon_maliyet ? `₺${d.porsiyon_maliyet.toFixed(2)}` : '-'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {d.satis_fiyati ? (
                        <span>
                          {`₺${d.satis_fiyati.toFixed(2)}`}
                          {d.pos_satis_fiyati && <span style={{ fontSize: '9px', color: '#1565c0', marginLeft: '4px', fontWeight: 'normal' }}>POS</span>}
                        </span>
                      ) : <span style={{ color: '#ccc' }}>-</span>}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {d.dukkan_kar != null ? (
                        <span style={{ color: d.dukkan_kar >= 0 ? '#2e7d32' : '#c62828' }}>
                          ₺{d.dukkan_kar.toFixed(2)}
                        </span>
                      ) : <span style={{ color: '#ccc' }}>-</span>}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      {d.dukkan_kar_pct != null ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: radius.lg, fontSize: '10px', fontWeight: 'bold',
                          background: karColor.bg, color: karColor.color
                        }}>%{d.dukkan_kar_pct}</span>
                      ) : <span style={{ color: '#ccc' }}>-</span>}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      {d.food_cost_pct != null ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: radius.lg, fontSize: '10px', fontWeight: 'bold',
                          background: fcColor.bg, color: fcColor.color
                        }}>%{d.food_cost_pct}</span>
                      ) : <span style={{ color: '#ccc' }}>-</span>}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr style={{ background: '#f8fafc', borderBottom: `1px solid ${colors.border}` }}>
                      <td colSpan={10} style={{ padding: '0' }}>
                        <RecipeDetailPanel detail={detail} loading={detailLoading} recipeId={d.id} recipe={d} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default FoodCostDashboard;
